import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, Star, Zap, Crown, Building2, ChevronDown, ChevronUp } from 'lucide-react';
import { TIERS, PricingTier } from '@/config/tiers';

interface SubscriptionPlanSelectorProps {
  selectedPlan: string;
  onPlanSelect: (planId: string) => void;
}

const SubscriptionPlanSelector: React.FC<SubscriptionPlanSelectorProps> = ({
  selectedPlan,
  onPlanSelect
}) => {
  const [expandedPlan, setExpandedPlan] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const tiers: Array<PricingTier & { icon: React.ReactNode; color: string }> = [
    {
      ...TIERS.free,
      icon: <Star className="w-6 h-6" />,
      color: 'gray'
    },
    {
      ...TIERS.basic,
      icon: <Zap className="w-6 h-6" />,
      color: 'blue'
    },
    {
      ...TIERS.premium,
      icon: <Crown className="w-6 h-6" />,
      color: 'purple'
    },
    {
      ...TIERS.enterprise,
      icon: <Building2 className="w-6 h-6" />,
      color: 'gold'
    }
  ];

  const getTierStyles = (tier: typeof tiers[0], isSelected: boolean) => {
    const baseStyles = 'relative p-5 lg:p-6 rounded-xl border-2 transition-all cursor-pointer h-full flex flex-col';
    const selectedStyles = isSelected ? 'ring-2 ring-offset-2' : '';
    
    switch (tier.color) {
      case 'gray':
        return `${baseStyles} ${selectedStyles} ${
          isSelected 
            ? 'border-gray-500 bg-gray-100 ring-gray-300' 
            : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
        }`;
      case 'blue':
        return `${baseStyles} ${selectedStyles} ${
          isSelected 
            ? 'border-blue-500 bg-blue-50 ring-blue-300' 
            : 'border-blue-200 bg-white hover:border-blue-300 hover:shadow-md'
        }`;
      case 'purple':
        return `${baseStyles} ${selectedStyles} ${
          isSelected 
            ? 'border-purple-500 bg-purple-50 ring-purple-300' 
            : 'border-purple-200 bg-white hover:border-purple-300 hover:shadow-md'
        }`;
      case 'gold':
        return `${baseStyles} ${selectedStyles} ${
          isSelected 
            ? 'border-yellow-500 bg-yellow-50 ring-yellow-300' 
            : 'border-yellow-200 bg-white hover:border-yellow-300 hover:shadow-md'
        }`;
      default:
        return `${baseStyles} ${selectedStyles} border-gray-200 bg-white hover:border-gray-300 hover:shadow-md`;
    }
  };

  const getIconColor = (color: string) => {
    switch (color) {
      case 'gray': return 'text-gray-600';
      case 'blue': return 'text-blue-600';
      case 'purple': return 'text-purple-600';
      case 'gold': return 'text-yellow-600';
      default: return 'text-gray-600';
    }
  };

  const toggleExpand = (planId: string) => {
    setExpandedPlan(expandedPlan === planId ? null : planId);
  };

  return (
    <div className="w-full">
      <div className="mb-4">
        <h4 className="text-lg font-semibold text-gray-900 mb-2">
          Choose Your Subscription Plan
        </h4>
        <p className="text-sm text-gray-600">
          Select a plan that fits your business needs. You can upgrade or downgrade anytime.
        </p>
      </div>

      {/* Plan Cards Container */}
      <div className="w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6">
          {tiers.map((tier) => {
            const isSelected = selectedPlan === tier.id;
            const isExpanded = expandedPlan === tier.id;

            return (
              <motion.div
                key={tier.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className={`w-full min-w-0 ${getTierStyles(tier, isSelected)}`}
                onClick={() => onPlanSelect(tier.id)}
              >
                {/* Popular Badge */}
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-purple-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      Popular
                    </span>
                  </div>
                )}

                {/* Selection Indicator */}
                {isSelected && (
                  <div className="absolute top-3 right-3">
                    <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  </div>
                )}

                {/* Plan Header */}
                <div className="text-center mb-4">
                  <div className={`inline-flex items-center justify-center w-12 h-12 lg:w-14 lg:h-14 rounded-full mb-3 ${
                    tier.color === 'gray' ? 'bg-gray-100' :
                    tier.color === 'blue' ? 'bg-blue-100' :
                    tier.color === 'purple' ? 'bg-purple-100' :
                    'bg-yellow-100'
                  }`}>
                    <div className={getIconColor(tier.color)}>
                      {tier.icon}
                    </div>
                  </div>
                  <h5 className="text-lg lg:text-xl font-bold text-gray-900 mb-1">{tier.name}</h5>
                  <div className="mb-2">
                    <span className="text-2xl lg:text-3xl font-bold text-gray-900">
                      ₹{tier.price.toLocaleString('en-IN')}
                    </span>
                    <span className="text-sm lg:text-base text-gray-600">/month</span>
                  </div>
                </div>

                {/* Key Features */}
                <div className="space-y-2 mb-4 flex-1">
                  {isExpanded ? (
                    // Show all features when expanded
                    tier.features.map((feature, index) => (
                      <div key={index} className="flex items-start">
                        <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-xs sm:text-sm lg:text-base text-gray-700 leading-relaxed">{feature}</span>
                      </div>
                    ))
                  ) : (
                    // Show only first 3 features when collapsed
                    tier.features.slice(0, 3).map((feature, index) => (
                      <div key={index} className="flex items-start">
                        <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-xs sm:text-sm lg:text-base text-gray-700 leading-relaxed">{feature}</span>
                      </div>
                    ))
                  )}
                </div>

                {/* Expand/Collapse Button */}
                {tier.features.length > 3 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpand(tier.id);
                    }}
                    className="w-full flex items-center justify-center text-xs sm:text-sm text-blue-600 hover:text-blue-800 font-medium py-2 border-t border-gray-200 mt-auto transition-colors"
                  >
                    {isExpanded ? (
                      <>
                        <span className="mr-1">Show Less</span>
                        <ChevronUp className="w-4 h-4" />
                      </>
                    ) : (
                      <>
                        <span className="mr-1">View All Features</span>
                        <ChevronDown className="w-4 h-4" />
                      </>
                    )}
                  </button>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Scroll Indicator - Only show on mobile if needed */}
      {isMobile && (
        <div className="mt-2 text-center">
          <p className="text-xs text-gray-500">
            Scroll down to see all plans
          </p>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPlanSelector;

